import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Modal,
  Snackbar,
  Alert,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  Chip,
  Stack,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import SidebarMenu from "./AdminPanel/SidebarMenu";
import TopBar from "./AdminPanel/TopBar";
import { useNavigate, useLocation } from "react-router-dom";
import FilterProduct from "./ProductForm/FilterProduct";
import xmlIcon from "../../static/media/icon/xml.svg";

function getCSRFToken() {
  const name = "csrftoken";
  const cookies = document.cookie.split(";");
  for (let c of cookies) {
    const t = c.trim();
    if (t.startsWith(name + "=")) return decodeURIComponent(t.substring(name.length + 1));
  }
  return null;
}

function normalize(text) {
  if (!text) return "";
  return String(text).normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
}

const ProductDocuments = {
  async downloadFile(url) {
    const response = await fetch(url, {
      method: "GET",
      credentials: "include",
    });

    if (!response.ok) {
      let message = "No se pudo descargar el archivo";
      try {
        const data = await response.json();
        message = data.error || message;
      } catch {}
      throw new Error(message);
    }

    const blob = await response.blob();
    const contentDisposition = response.headers.get("Content-Disposition") || "";
    const match = contentDisposition.match(/filename="?(.*?)"?$/i);
    const filename = match?.[1] || "reporte_productos.xlsx";

    const blobUrl = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(blobUrl);
  },

  delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  },
};

const tableHeadSx = {
  bgcolor: "#1565C0",
  color: "#fff",
  fontWeight: 700,
  whiteSpace: "nowrap",
  py: 1.15,
  fontSize: 14,
};

const compactTextSx = {
  fontSize: 13,
  lineHeight: 1.2,
};

const money = (value) => `$${Number(value || 0).toFixed(2)}`;

const getSectionId = (p) =>
  p?.section_id ??
  p?.section?.id ??
  p?.section?.pk ??
  p?.section;

const getSectionName = (p) =>
  p?.section_name ??
  p?.section?.name ??
  p?.section_nombre ??
  p?.section_label ??
  (typeof p?.section === "string" ? p.section : "");

const getCategoryId = (p) =>
  p?.category_id ??
  p?.category?.id ??
  p?.category?.pk ??
  p?.category;

const getCategoryName = (p) =>
  p?.category_name ??
  p?.category?.name ??
  p?.category_nombre ??
  p?.category_label ??
  (typeof p?.category === "string" ? p.category : "");

const getBrandId = (p) =>
  p?.brand_id ??
  p?.brand?.id ??
  p?.brand?.pk ??
  p?.brand;

const getBrandName = (p) =>
  p?.brand_name ??
  p?.brand?.name ??
  p?.brand_nombre ??
  p?.brand_label ??
  (typeof p?.brand === "string" ? p.brand : "");

const AdminProduct = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [open, setOpen] = useState(false);
  const [submenuOpen, setSubmenuOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sectionFilter, setSectionFilter] = useState("TODAS");
  const [categoryFilter, setCategoryFilter] = useState("TODAS");
  const [brandFilter, setBrandFilter] = useState("TODAS");
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [deleteProductId, setDeleteProductId] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarType, setSnackbarType] = useState("success");
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [excelAnchor, setExcelAnchor] = useState(null);
  const [downloadModal, setDownloadModal] = useState({
    open: false,
    title: "Descargando reporte",
    message: "Generando el documento…",
  });

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const r = await fetch("/productos/ver-productos/", { credentials: "include" });
      const d = await r.json().catch(() => ({}));
      if (r.ok) setProducts(d.products || []);
      else setProducts([]);
    } catch {
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    const s = location.state && location.state.snackbar;
    if (s && s.message) {
      setSnackbarType(s.type || "success");
      setSnackbarMessage(s.message);
      setSnackbarOpen(true);
      navigate(".", { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  useEffect(() => {
    setPage(0);
  }, [searchTerm, sectionFilter, categoryFilter, brandFilter]);

  const sections = useMemo(() => {
    const map = new Map();
    products.forEach((p) => {
      const id = getSectionId(p);
      const name = getSectionName(p);
      if (id !== undefined && id !== null && name) {
        map.set(String(id), { id: String(id), name });
      }
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [products]);

  const categories = useMemo(() => {
    const map = new Map();
    products.forEach((p) => {
      const secId = String(getSectionId(p) ?? "");
      const id = getCategoryId(p);
      const name = getCategoryName(p);
      if (id !== undefined && id !== null && name) {
        if (sectionFilter === "TODAS" || String(sectionFilter) === secId) {
          map.set(String(id), { id: String(id), name, sectionId: secId });
        }
      }
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [products, sectionFilter]);

  const brands = useMemo(() => {
    const map = new Map();
    products.forEach((p) => {
      const id = getBrandId(p);
      const name = getBrandName(p);
      if (id !== undefined && id !== null && name) {
        map.set(String(id), { id: String(id), name });
      }
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [products]);

  const openDeleteModal = (id) => {
    setDeleteProductId(id);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    const csrf = getCSRFToken();
    const r = await fetch(`/productos/eliminar-producto/${deleteProductId}/`, {
      method: "DELETE",
      headers: { "X-CSRFToken": csrf },
      credentials: "include",
    });

    if (r.ok) {
      fetchProducts();
      setSnackbarType("success");
      setSnackbarMessage("Producto eliminado correctamente");
      setSnackbarOpen(true);
    } else {
      const d = await r.json().catch(() => ({}));
      setSnackbarType("error");
      setSnackbarMessage(d.error || "No se pudo eliminar el producto");
      setSnackbarOpen(true);
    }

    setDeleteModalOpen(false);
  };

  const openExcelMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setExcelAnchor(e.currentTarget);
  };

  const closeExcelMenu = () => {
    setExcelAnchor(null);
  };

  const startDownload = async (mode) => {
    setDownloadModal({
      open: true,
      title: "Descargando reporte",
      message: "Generando el documento…",
    });

    const params = new URLSearchParams({
      search: searchTerm,
      section: sectionFilter !== "TODAS" ? sectionFilter : "",
      category: categoryFilter !== "TODAS" ? categoryFilter : "",
      brand: brandFilter !== "TODAS" ? brandFilter : "",
    });

    const url =
      mode === "DETALLADO"
        ? `/productos/excel/detallado/?${params.toString()}`
        : `/productos/excel/?${params.toString()}`;

    try {
      await ProductDocuments.downloadFile(url);
      await ProductDocuments.delay(1500);
    } catch (e) {
      setSnackbarType("error");
      setSnackbarMessage(e?.message || "Error al descargar");
      setSnackbarOpen(true);
    } finally {
      setDownloadModal((s) => ({ ...s, open: false }));
      closeExcelMenu();
    }
  };

  const term = normalize(searchTerm);

  const filteredProducts = useMemo(() => {
    return products.filter((p) => {
      const searchPool = [
        p?.name,
        p?.code,
        p?.barcode,
        p?.codigo_auxiliar,
        getSectionName(p),
        getCategoryName(p),
        getBrandName(p),
      ]
        .map(normalize)
        .join(" ");

      const matchesSearch = !term || searchPool.includes(term);
      const matchesSection =
        sectionFilter === "TODAS" || String(getSectionId(p) ?? "") === String(sectionFilter);
      const matchesCategory =
        categoryFilter === "TODAS" || String(getCategoryId(p) ?? "") === String(categoryFilter);
      const matchesBrand =
        brandFilter === "TODAS" || String(getBrandId(p) ?? "") === String(brandFilter);

      return matchesSearch && matchesSection && matchesCategory && matchesBrand;
    });
  }, [products, term, sectionFilter, categoryFilter, brandFilter]);

  const handleSelect = (c) => {
    if (c === "AdminSection") navigate("/admin-panel/secciones");
    else if (c === "ModalInventario") setSubmenuOpen((s) => !s);
    else if (c === "AdminProduct") navigate("/admin-panel/inventario/productos");
    else if (c === "AdminVariant") navigate("/admin-panel/inventario/variantes");
    else if (c === "AdminProforma") navigate("/admin-panel/proformas");
    else if (c === "AdminClient") navigate("/admin-panel/clientes");
    else if (c === "AdminServices") navigate("/admin-panel/servicios");
    else if (c === "AdminPromotion") navigate("/admin-panel/promociones");
    else navigate("/admin-panel");
  };

  const handleLogout = async () => {
    await fetch("/sistema/logout/", { method: "POST", credentials: "include" });
    localStorage.clear();
    navigate("/login");
  };

  const clearFilters = () => {
    setSearchTerm("");
    setSectionFilter("TODAS");
    setCategoryFilter("TODAS");
    setBrandFilter("TODAS");
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#f5f6fa" }}>
      <SidebarMenu
        open={open}
        setOpen={setOpen}
        submenuOpen={submenuOpen}
        setSubmenuOpen={setSubmenuOpen}
        handleSelect={handleSelect}
        handleLogout={handleLogout}
        navigate={navigate}
      />

      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <TopBar open={open} setOpen={setOpen} setSelectedComponent={() => navigate("/admin-panel")} />

        <Box sx={{ width: "95%", mx: "auto", mt: 3, mb: 5 }}>
          <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 3 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              sx={{
                textTransform: "none",
                backgroundColor: "#1565C0",
                borderRadius: 2,
                px: 2.2,
                fontWeight: 700,
                boxShadow: "0 8px 20px rgba(21,101,192,0.22)",
              }}
              onClick={() => navigate("nuevo")}
            >
              Nuevo Producto
            </Button>
          </Box>

          <Paper sx={{ p: 2.5, borderRadius: 3, boxShadow: "0 10px 30px rgba(15,23,42,0.06)" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: { xs: "flex-start", md: "center" },
                justifyContent: "space-between",
                gap: 2,
                mb: 2,
                flexWrap: "wrap",
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: 19, color: "#0f172a" }}>
                  Productos
                </Typography>
                <Typography sx={{ fontSize: 13, color: "#64748b", mt: 0.4 }}>
                  Gestiona tu catálogo con filtros por sección, categoría y marca
                </Typography>
              </Box>

              <Tooltip title="Descargar reporte" arrow>
                <IconButton
                  onClick={openExcelMenu}
                  sx={{
                    p: 0.7,
                    borderRadius: 2,
                    border: "1px solid #e2e8f0",
                    bgcolor: "#fff",
                    "&:hover": { bgcolor: "#f8fafc" },
                  }}
                >
                  <img src={xmlIcon} alt="Descargar reporte" style={{ width: 26, height: 26 }} />
                </IconButton>
              </Tooltip>
            </Box>

            <FilterProduct
              search={searchTerm}
              setSearch={setSearchTerm}
              sectionFilter={sectionFilter}
              setSectionFilter={setSectionFilter}
              categoryFilter={categoryFilter}
              setCategoryFilter={setCategoryFilter}
              brandFilter={brandFilter}
              setBrandFilter={setBrandFilter}
              sections={sections}
              categories={categories}
              brands={brands}
              onClear={clearFilters}
            />

            <TableContainer
              sx={{
                borderRadius: 2,
                border: "1px solid #e2e8f0",
                overflowX: "auto",
                overflowY: "hidden",
                maxWidth: "100%",
                "&::-webkit-scrollbar": { height: 10 },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#cbd5e1",
                  borderRadius: 10,
                },
              }}
            >
              <Table size="small" sx={{ minWidth: 980, tableLayout: "auto" }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...tableHeadSx, minWidth: 300 }}>Producto</TableCell>
                    <TableCell sx={{ ...tableHeadSx, minWidth: 120 }}>Código</TableCell>
                    <TableCell sx={{ ...tableHeadSx, minWidth: 150 }}>Clasificación</TableCell>
                    <TableCell sx={{ ...tableHeadSx, minWidth: 110 }} align="right">
                      PVP1
                    </TableCell>
                    <TableCell sx={{ ...tableHeadSx, minWidth: 120 }} align="center">
                      IVA
                    </TableCell>
                    <TableCell sx={{ ...tableHeadSx, minWidth: 120 }} align="center">
                      Stock
                    </TableCell>
                    <TableCell sx={{ ...tableHeadSx, minWidth: 150 }} align="center">
                      Acción
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                        <CircularProgress size={26} />
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredProducts
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((p) => {
                        const stockMinimo = Number(p.stock_minimo ?? 0);
                        const stockActual = Number(p.stock ?? 0);
                        const lowStock = stockActual <= stockMinimo;

                        return (
                          <TableRow
                            key={p.id}
                            hover
                            sx={{
                              "& td": { py: 1, borderBottom: "1px solid #edf2f7" },
                              "&:hover": { bgcolor: "#fafcff" },
                            }}
                          >
                            <TableCell sx={{ minWidth: 300 }}>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography
                                  sx={{
                                    ...compactTextSx,
                                    fontWeight: 700,
                                    color: "#0f172a",
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                  }}
                                >
                                  {p.name}
                                </Typography>

                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.7, flexWrap: "wrap", mt: 0.65 }}>
                                  <Chip
                                    label={p.estado === "activo" ? "Activo" : "Inactivo"}
                                    size="small"
                                    color={p.estado === "activo" ? "success" : "default"}
                                    variant={p.estado === "activo" ? "filled" : "outlined"}
                                    sx={{
                                      fontWeight: 700,
                                      borderRadius: 2,
                                      height: 22,
                                      "& .MuiChip-label": { px: 1, fontSize: 10.5 },
                                    }}
                                  />

                                  {p.has_variants && (
                                    <Chip
                                      label={p.variant_type ? `Variantes: ${p.variant_type}` : "Con variantes"}
                                      size="small"
                                      color="info"
                                      variant="outlined"
                                      sx={{
                                        fontWeight: 700,
                                        borderRadius: 2,
                                        height: 22,
                                        "& .MuiChip-label": { px: 1, fontSize: 10.5 },
                                      }}
                                    />
                                  )}
                                </Box>

                                {!!p.barcode && (
                                  <Typography sx={{ fontSize: 11, color: "#64748b", mt: 0.35 }}>
                                    Código de barras: {p.barcode}
                                  </Typography>
                                )}

                                {!!p.codigo_auxiliar && (
                                  <Typography sx={{ fontSize: 11, color: "#64748b", mt: 0.2 }}>
                                    Auxiliar: {p.codigo_auxiliar}
                                  </Typography>
                                )}
                              </Box>
                            </TableCell>

                            <TableCell sx={{ minWidth: 120 }}>
                              <Typography sx={{ ...compactTextSx, fontWeight: 700, color: "#1565C0" }}>
                                {p.code || "-"}
                              </Typography>
                            </TableCell>

                            <TableCell sx={{ minWidth: 150 }}>
                              <Stack spacing={0.6}>
                                <Typography sx={{ fontSize: 11.5, color: "#0f172a", fontWeight: 600 }}>
                                  {getSectionName(p) || "-"}
                                </Typography>

                                <Typography sx={{ fontSize: 11.5, color: "#64748b" }}>
                                  {getCategoryName(p) || "-"}
                                </Typography>

                                <Typography sx={{ fontSize: 11.5, color: "#64748b" }}>
                                  {getBrandName(p) || "-"}
                                </Typography>
                              </Stack>
                            </TableCell>

                            <TableCell align="right" sx={{ fontWeight: 700, color: "#0f172a", fontSize: 13 }}>
                              {money(p.pvp1)}
                            </TableCell>

                            <TableCell align="center" sx={{ minWidth: 120 }}>
                              <Stack spacing={0.5} alignItems="center">
                                <Chip
                                  label={p.iva_porcentaje === 999 ? "No IVA" : `${p.iva_porcentaje}%`}
                                  size="small"
                                  color={p.iva_porcentaje === 999 ? "default" : "info"}
                                  variant={p.iva_porcentaje === 999 ? "outlined" : "filled"}
                                  sx={{
                                    fontWeight: 700,
                                    borderRadius: 2,
                                    height: 24,
                                    "& .MuiChip-label": { px: 1, fontSize: 11 },
                                  }}
                                />
                                <Typography sx={{ fontSize: 11, color: "#64748b" }}>
                                  {money(p.iva_valor)}
                                </Typography>
                              </Stack>
                            </TableCell>

                            <TableCell align="center" sx={{ minWidth: 120 }}>
                              <Stack spacing={0.45} alignItems="center">
                                <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>
                                  {stockActual}
                                </Typography>
                                <Chip
                                  label={`Min: ${p.stock_minimo ?? 0}`}
                                  size="small"
                                  color={lowStock ? "error" : "success"}
                                  variant={lowStock ? "filled" : "outlined"}
                                  sx={{
                                    fontWeight: 700,
                                    borderRadius: 2,
                                    height: 24,
                                    "& .MuiChip-label": { px: 1, fontSize: 11 },
                                  }}
                                />
                              </Stack>
                            </TableCell>

                            <TableCell align="center" sx={{ minWidth: 150 }}>
                              <Box sx={{ display: "flex", gap: 1, justifyContent: "center", flexWrap: "wrap" }}>
                                <Tooltip title="Abrir dashboard" arrow>
                                  <IconButton
                                    size="small"
                                    onClick={() => navigate(`/admin-panel/inventario/productos/${p.id}`)}
                                    sx={{
                                      color: "#1565C0",
                                      borderRadius: 2,
                                      border: "1px solid #dbeafe",
                                      bgcolor: "#f8fbff",
                                      "&:hover": { bgcolor: "#eef6ff" },
                                    }}
                                  >
                                    <DashboardOutlinedIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Tooltip>

                                <Tooltip title="Eliminar producto" arrow>
                                  <IconButton
                                    size="small"
                                    onClick={() => openDeleteModal(p.id)}
                                    sx={{
                                      color: "#d32f2f",
                                      borderRadius: 2,
                                      border: "1px solid #fecaca",
                                      bgcolor: "#fff7f7",
                                      "&:hover": { bgcolor: "#ffefef" },
                                    }}
                                  >
                                    <DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })
                  )}

                  {!loading && !filteredProducts.length && (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                        <Stack spacing={0.75} alignItems="center">
                          <Typography sx={{ fontWeight: 700, color: "#0f172a" }}>
                            No hay productos para mostrar
                          </Typography>
                          <Typography sx={{ fontSize: 13, color: "#64748b" }}>
                            Ajusta los filtros o cambia la búsqueda
                          </Typography>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={filteredProducts.length}
              page={page}
              onPageChange={(e, np) => setPage(np)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 20, 25, 50]}
              labelRowsPerPage="Filas por página:"
            />
          </Paper>
        </Box>
      </Box>

      <Menu
        anchorEl={excelAnchor}
        open={Boolean(excelAnchor)}
        onClose={closeExcelMenu}
        disableScrollLock
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            startDownload("RESUMEN");
          }}
          sx={{ fontWeight: 600 }}
        >
          Documento Excel
        </MenuItem>
        <MenuItem
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            startDownload("DETALLADO");
          }}
          sx={{ fontWeight: 600 }}
        >
          Documento Excel detallado
        </MenuItem>
      </Menu>

      <Dialog open={downloadModal.open} onClose={() => {}} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{downloadModal.title}</DialogTitle>
        <DialogContent sx={{ display: "flex", alignItems: "center", gap: 2, py: 3 }}>
          <CircularProgress />
          <Typography sx={{ fontWeight: 600 }}>{downloadModal.message}</Typography>
        </DialogContent>
      </Dialog>

      <Modal open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "white",
            p: 4,
            borderRadius: 3,
            width: 360,
            boxShadow: "0 20px 60px rgba(15,23,42,0.22)",
          }}
        >
          <Typography variant="h6" mb={1.5} sx={{ fontWeight: 700 }}>
            ¿Eliminar producto?
          </Typography>
          <Typography mb={3} sx={{ color: "#475569" }}>
            Esta acción no se puede deshacer.
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button variant="outlined" onClick={() => setDeleteModalOpen(false)}>
              Cancelar
            </Button>
            <Button variant="contained" color="error" onClick={confirmDelete}>
              Eliminar
            </Button>
          </Box>
        </Box>
      </Modal>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarType} sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminProduct;